/**
 * Subscription Tier Logger
 *
 * This utility provides consistent logging for subscription tier
 * information throughout the application. It's designed to help
 * track subscription tier data as it flows from the API to the
 * client application.
 */

// Define the log prefix for easy filtering in console
const LOG_PREFIX = "💲 [SUBSCRIPTION TIER]";

// Check if we're in development mode for conditional logging
const isDev = process.env.NODE_ENV === "development";

/**
 * Log subscription tier information
 * @param context The source context (where the log is coming from)
 * @param message The log message
 * @param data Additional data to log
 */
export const logSubscription = (context: string, message: string, data?: any) => {
  if (!isDev) return; // Only log in development mode

  console.group(`${LOG_PREFIX} ${context}`);
  console.log(message);

  if (data) {
    console.log("Data:", data);
  }

  console.groupEnd();
};

/**
 * Log API response subscription tier
 * @param userData The user data returned from the API
 */
export const logApiResponse = (userData: any) => {
  if (!isDev || !userData) return;

  logSubscription("API Response", "User data received from API", {
    userId: userData.id,
    email: userData.email,
    tier: userData.subscriptionTier,
  });
};

/**
 * Log store subscription tier
 * @param storeState The current auth store state
 */
export const logStoreState = (storeState: any) => {
  if (!isDev) return;

  const user = storeState.user;

  logSubscription("Auth Store", "Current auth store state", {
    userTier: user?.subscriptionTier,
    getterTier: storeState.subscriptionTier,
    userId: user?.id,
    email: user?.email,
    hasUser: !!user,
  });
};

/**
 * Log subscription tier mismatch
 * @param apiTier The tier from the API
 * @param storeTier The tier from the store
 */
export const logTierMismatch = (apiTier: string, storeTier: string) => {
  if (!isDev) return;

  console.warn(
    `${LOG_PREFIX} MISMATCH: API tier (${apiTier}) doesn't match store tier (${storeTier})!`,
  );
};

/**
 * Log component render with subscription tier
 * @param component The component name
 * @param tier The subscription tier
 */
export const logComponentRender = (component: string, tier: string) => {
  if (!isDev) return;

  console.log(`${LOG_PREFIX} Component [${component}] rendered with subscription tier: ${tier}`);
};

/**
 * Deep debug function to analyze API response vs client processed data
 * This will help diagnose serialization/deserialization issues
 * @param apiData The raw API response data
 */
export const debugUserStructure = (apiData: any) => {
  if (!isDev || !apiData) return;

  console.group(`${LOG_PREFIX} DEEP DEBUG - User Object Structure`);

  console.log("Object type:", typeof apiData);
  console.log("Is Array?", Array.isArray(apiData));
  console.log("Constructor name:", apiData.constructor?.name);
  console.log('hasOwnProperty("subscriptionTier"):', apiData.hasOwnProperty?.("subscriptionTier"));
  console.log("Keys on object:", Object.keys(apiData));

  // Check for property descriptors
  console.log(
    "Property descriptor for subscriptionTier:",
    Object.getOwnPropertyDescriptor(apiData, "subscriptionTier"),
  );

  // List all property values
  console.log("All properties and values:");
  for (const key of Object.keys(apiData)) {
    console.log(`  - ${key}: ${apiData[key]} (${typeof apiData[key]})`);
  }

  // Check prototype chain
  let proto = Object.getPrototypeOf(apiData);
  const protoChain = [];
  while (proto) {
    protoChain.push(proto.constructor?.name);
    proto = Object.getPrototypeOf(proto);
  }
  console.log("Prototype chain:", protoChain);

  // Try to access with various methods
  console.log("Direct access:", apiData.subscriptionTier);
  console.log("Bracket access:", apiData.subscriptionTier);
  console.log("getOwnPropertyNames:", Object.getOwnPropertyNames(apiData));

  // Check if values get serialized properly
  console.log("JSON.stringify result:", JSON.stringify(apiData));

  console.groupEnd();
};

export default {
  logSubscription,
  logApiResponse,
  logStoreState,
  logTierMismatch,
  logComponentRender,
  debugUserStructure,
};
